import { Box, capitalize, Grid, SxProps, Typography, useTheme } from '@mui/material';
import { useMemo, useState, type JSX } from 'react';
import { useAtom } from 'ximple';
import { useLocale, getLocaleNumberFormatTwoDecimals } from '@repo/i18n';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import {
    calculateAppliedGiftcards,
    getPriceSummaryWithNativeVat,
    PriceSummary,
} from '@repo/widget-utils/price-helper';
import { useAtomReducer } from 'src/hooks';
import { voucherAtom, setGiftcards } from 'src/state/voucher/voucherAtom';
import { paymentPlanAtom } from 'src/state/payment-plan/paymentPlanAtom';
import { ShareCartButton } from 'src/widgets/activities/share-cart-button/ShareCartButton';
import { SharePanel } from 'src/widgets/activities/share-cart-button/SharePanel';
import SummaryBooking from './SummaryBooking';
import SummaryApplyGiftCardPayments from './SummaryApplyGiftCardPayments';
import SummaryPayment from './SummaryPayment';
import SummaryPromoCode from './SummaryPromoCode';
import TypographyStyleUtils from 'src/components/utils/jss/TypographyStyleUtils';
import SummaryVat from './SummaryVat';
import { showError } from 'src/utils/widget/error-handling';
import { sumBy, uniqBy } from 'lodash-es';
import { BilberryGiftcardStatus, AppliedGiftCard } from '@repo/types';
import { SummaryCartItem } from './summary-mapping';
import { useMembershipMode } from '@repo/widget-utils/widgetsConfiguration';
import { nativeMemberCoverageAtom } from 'src/state/native-membership/nativeMemberCoverageAtom';

interface IProps {
    heading?: string;
    features: SummaryFeatures;
    priceSummary: PriceSummary;
    summaryCartItemsByType: Record<string, SummaryCartItem[]>;
    onRemoveCartItem: (summaryCartItem: SummaryCartItem) => void;
    onEditClick: (e: React.MouseEvent, summaryCartItem: SummaryCartItem) => void;
    onClearCart?: () => void;
}

export type SummaryFeatures = {
    showShare?: boolean;
    invertedColorsClass?: Record<string, unknown>;
    showPromoCodeSection: boolean;
    promoCodeSectionSx?: SxProps;
    promoCodeWritable: boolean;
    giftcardWriteable: boolean;
    enableCartClearing: boolean;
    enableItemEditing: boolean;
    showGiftcardValue: boolean;
};

export default function Summary(props: IProps): JSX.Element {
    const [{ giftcards }, voucherDispatch] = useAtomReducer(voucherAtom);
    const [{ isUsingPaymentPlan, isLoadingPrices }] = useAtom(paymentPlanAtom);

    const { features, priceSummary, summaryCartItemsByType } = props;

    const appliedGiftcards = calculateAppliedGiftcards(giftcards, priceSummary.totalPrice);

    const priceReductionGiftCard = sumBy(appliedGiftcards, (x) => x.priceReduction);

    const totalPriceWithPriceReduction = Math.max(
        0,
        priceSummary.totalPrice - priceReductionGiftCard,
    );

    const isGiftcardDeactivatedDueToDeposit = !!priceSummary.priceToPayLater;

    const handleApplyGiftcard = (giftcard: BilberryGiftcardStatus) => {
        const oldGiftcardStatuses = appliedGiftcards.map((x) => x.giftcardStatus);
        const newGiftcardStatuses = uniqBy([...oldGiftcardStatuses, giftcard], (x) => x.id);

        voucherDispatch(setGiftcards(newGiftcardStatuses));
    };

    const handleRemoveAppliedGiftcard = (appliedGiftcard: AppliedGiftCard) => {
        const newGiftcardStatuses = appliedGiftcards
            .filter((x) => x !== appliedGiftcard)
            .map((x) => x.giftcardStatus);

        voucherDispatch(setGiftcards(newGiftcardStatuses));
    };

    const currentPromoCodePriceReduction = useMemo(() => {
        const { promoCodeDiscounts } = priceSummary;
        return promoCodeDiscounts.reduce((acc, cur) => acc + cur.amount, 0);
    }, [priceSummary]);

    const hasGiftcardValueToDisplay = features.showGiftcardValue && appliedGiftcards?.length > 0;

    // Native membership: when a member's cards apply, show the coverage and use Base's
    // authoritative total (from /api/reserve/calculate) so the displayed total matches the charge.
    // Inert for guest/legacy tenants (result is null).
    const [{ result: nativeCoverageResult, committedResult, isCalculating: nativeIsCalculating }] =
        useAtom(nativeMemberCoverageAtom);
    const isNativeMode = useMembershipMode() === 'native';
    // Prefer the live result during checkout; on the confirmation/receipt the cart (and live result)
    // is cleared, so fall back to the snapshot taken at reserve — keeping the member-card coverage +
    // actual total instead of the gross. (Live-first avoids a stale total after a cancelled payment.)
    const nativeResult = isNativeMode ? (nativeCoverageResult ?? committedResult) : null;
    const isNativeUpdating = isNativeMode && nativeIsCalculating;
    const displayedTotal = nativeResult ? nativeResult.total : totalPriceWithPriceReduction;
    // While /calculate is in flight we don't yet know the member's price — drive the existing loading
    // UX (skeleton lines, hidden totals) so the gross price never flashes before the discount.
    const pricesLoading = isLoadingPrices || isNativeUpdating;
    // When a native member's cards fully cover the cart, the gross-dependent rows (VAT, promo,
    // giftcard) have nothing to act on — hide them, matching the existing zero-total behavior.
    // The !nativeResult short-circuit keeps guest/legacy byte-identical.
    const showGrossDependentRows =
        priceSummary.totalPrice > 0 && (!nativeResult || displayedTotal > 0);
    // Native mode shows Base's coverage-aware VAT (from /api/reserve/calculate) instead of the cart's
    // gross VAT, so the MVA rows reflect the member-card discount. No-op for guest/legacy.
    const vatPriceSummary = nativeResult
        ? getPriceSummaryWithNativeVat(priceSummary, nativeResult)
        : priceSummary;

    return (
        <Grid>
            <HeaderSection
                heading={props.heading}
                invertedColors={!!features.invertedColorsClass}
                invertedColorsClass={features.invertedColorsClass}
                showShare={features.showShare}
            />

            <SummaryBooking
                invertedColorsClass={features.invertedColorsClass}
                summaryCartItemsByType={summaryCartItemsByType}
                enableItemEditing={features.enableItemEditing}
                enableCartClearing={features.enableCartClearing}
                isUsingPaymentPlan={isUsingPaymentPlan}
                isLoadingPrices={pricesLoading}
                onEditClick={props.onEditClick}
                onRemoveCartItem={!features.enableCartClearing ? undefined : props.onRemoveCartItem}
                onClearCart={props.onClearCart}
            />

            {!pricesLoading && (
                <>
                    {showGrossDependentRows && (
                        <SummaryVat
                            priceSummary={vatPriceSummary}
                            invertedColorsClass={features.invertedColorsClass}
                        />
                    )}
                    {features.showPromoCodeSection && showGrossDependentRows && (
                        <SummaryPromoCode
                            sx={features.promoCodeSectionSx}
                            invertedColorsClass={features.invertedColorsClass}
                            currentPriceReduction={currentPromoCodePriceReduction}
                            writeable={features.promoCodeWritable}
                        />
                    )}
                    {features.giftcardWriteable && showGrossDependentRows && (
                        <SummaryApplyGiftCardPayments
                            appliedGiftcards={appliedGiftcards}
                            onApplyGiftcard={handleApplyGiftcard}
                            onRemoveAppliedGiftcard={handleRemoveAppliedGiftcard}
                            onError={showError}
                            isDeactivatedDueToDeposits={isGiftcardDeactivatedDueToDeposit}
                        />
                    )}
                    {hasGiftcardValueToDisplay && (
                        <GiftCardValueSection
                            giftcardValue={priceReductionGiftCard}
                        ></GiftCardValueSection>
                    )}

                    <Grid container direction="column" justifyContent="space-between" wrap="nowrap">
                        <SummaryPayment
                            invertedColorsClass={features.invertedColorsClass}
                            totalPrice={displayedTotal}
                            priceToPayLater={priceSummary.priceToPayLater}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
}

function HeaderSection(props: {
    heading?: string;
    invertedColors?: boolean;
    showShare?: boolean;
    invertedColorsClass?: Record<string, unknown>;
}) {
    const { t } = useLocale();
    const theme = useTheme();
    const customizations = useCustomizations();
    const [shareDialogVisible, setShareDialogVisible] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const primaryColorsClass = props.invertedColors
        ? { color: customizations.bookingWidgetPrimaryColor }
        : {};

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateRows: 'auto auto',
                gridTemplateColumns: '1fr 1fr',
                gap: theme.spacing(1),
                pb: theme.spacing(2),
            }}
        >
            <Typography
                component="h2"
                color="textSecondary"
                variant="h4"
                fontWeight={500}
                fontSize={28}
                sx={{ ...props.invertedColorsClass, ...primaryColorsClass, flexGrow: 1 }}
            >
                {props.heading ?? t.summary}
            </Typography>
            {props.showShare && (
                <ShareCartButton
                    hasInvertedColors={props.invertedColors}
                    setShareUrl={setShareUrl}
                    shareDialogVisible={shareDialogVisible}
                    setShareDialogVisible={setShareDialogVisible}
                    sx={{
                        mr: props.invertedColors ? theme.spacing(4) : 0,
                        justifySelf: 'end',
                    }}
                />
            )}
            {props.showShare && (
                <SharePanel
                    url={shareUrl}
                    visible={shareDialogVisible}
                    invertedColorsClass={props.invertedColorsClass}
                    sx={{ gridRow: 2, gridColumn: '1 / 3' }}
                />
            )}
        </Box>
    );
}

function GiftCardValueSection(props: { giftcardValue: number }) {
    const { t, locale } = useLocale();
    return (
        <Grid container direction="row" justifyContent="space-between" wrap="nowrap">
            <Grid
                container
                direction="column"
                justifyContent="space-between"
                wrap="nowrap"
                flex="3"
                flexShrink="1"
            >
                <Typography align="right" sx={TypographyStyleUtils.secondaryTextColorSx}>
                    {capitalize(t.gift_card)}
                </Typography>
            </Grid>

            <Grid
                container
                direction="column"
                justifyContent="space-between"
                wrap="nowrap"
                flex="1"
                flexGrow="1"
            >
                <Typography align="right" sx={TypographyStyleUtils.secondaryTextColorSx}>
                    -{getLocaleNumberFormatTwoDecimals(locale, props.giftcardValue)}
                </Typography>
            </Grid>
        </Grid>
    );
}
